/* HELPERS */
import create_readable_id from "./create_readable_id";
import { setCreativeTTB } from "./time_to_build";
import { create_asset_filename } from "./create_asset_filename";
/* LIBS */
import { s3_signed_url, s3_media_upload } from "../libs/amplify";
import { logger } from "../libs/logger";
import { Google_Analytics_Event } from "../libs/google-analytics";
/* TYPES */
import { ContextActions } from "../types/contextType";
import { User } from "../types/user.context";

interface AddFont {
  file?: File;
  teamId: string;
  creativeId: string;
  creativeDispatch: React.Dispatch<ContextActions>;
  formatType: string;
  mimeType: string;
  name: string;
  user: User;
  notificationDispatch: (action: NotificationAction) => void;
  mediaLoadingDispatch: (action: MediaLoadingAction) => void;
}

interface MediaLoadingAction {
  type: string;
  payload: {
    status?: boolean;
    type?: string;
    message?: string;
    progress?: number;
  };
}

interface NotificationAction {
  type: string;
  payload: {
    header: string;
    message: string;
    type: string;
    autoDismiss: boolean;
  };
}

export default async function add_font({ file, teamId, creativeId, creativeDispatch, formatType, mimeType, name, user, notificationDispatch, mediaLoadingDispatch }: AddFont) {
  let fileName = create_asset_filename({ name, backupFileName: "font" });
  const fileId = create_readable_id(4);
  try {
    mediaLoadingDispatch({ type: "UPDATE_PROGRESS", payload: { progress: Math.floor(Math.random() * (20 - 11 + 1) + 11) } });
    /* If no format exists, exit the process */
    if (!formatType) {
      throw new Error("No format chosen");
    }
    /* If no file exists, exit the upload process */
    if (!file) {
      throw new Error("No file found");
    }
    /* If file size = 0 (usually when trying to upload an un-synced file), exit the upload process */
    if (file.size === 0) {
      throw new Error("File has missing data. Please check file before uploading.");
    }

    const startTime = performance.now();
    /* CREATE & UPLOAD ORIGINAL FILES */
    let originalS3Upload = await s3_media_upload({
      fileKey: `${fileName}-${fileId}-original.${mimeType.split("/")[1]}`,
      mimeType,
      body: file,
      teamId,
      creativeId,
    });
    /**
     * HTL (09-10-23)
     * Time to build stats
     */
    const endTime = performance.now();
    const responseTime = endTime - startTime;
    setCreativeTTB("fontUpload", responseTime.toString());
    /**
     * JA (09-08-23)
     * Log error message if user is experiencing a font file being uploaded with an undefined mime type
     */
    if (!mimeType.split("/")[1]) {
      logger({ error: "Mime type is undefined on upload", user, context: { origin: "add_font_add_font", input: mimeType } });
      Google_Analytics_Event({ name: "error", category: "Mime type undefined on upload", action: "add_font_add_font", label: user.email });
    }

    mediaLoadingDispatch({ type: "UPDATE_PROGRESS", payload: { progress: Math.floor(Math.random() * (40 - 21 + 1) + 21) } });

    let originalUrl = await s3_signed_url({ teamId, creativeId, fileName: originalS3Upload.fileName });

    /* UPDATE CONTEXT */
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FONT_MIMETYPE",
      payload: { mimeType },
    });
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FONT_ID",
      payload: { id: fileId },
    });
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FONT_FILENAME",
      payload: { fileName },
    });
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FONT_BODYORIGINAL",
      payload: { bodyOriginal: originalUrl },
    });
  } catch (error) {
    return notificationDispatch({
      type: "ADD",
      payload: {
        header: "Error uploading",
        message: error.message,
        type: "error",
        autoDismiss: true,
      },
    });
  }
}
